import type { ReschedulingReason } from '../types';
import { RESCHEDULING_REASONS } from '../types';
import { RESCHEDULING_REASON_TRANSLATION_KEYS } from '../rescheduling-reason-labels';

import { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';

const REASON_TRANSLATION_KEYS = RESCHEDULING_REASON_TRANSLATION_KEYS;

interface Step1Props {
  selectedReason?: ReschedulingReason | null;
  defaultSelectedReason?: ReschedulingReason | null;
  note?: string;
  onNoteChange?: (note: string) => void;
  onReasonChange?: (reason: ReschedulingReason) => void;
  onNext?: () => void;
}

export const Step1 = memo(
  ({
    selectedReason,
    defaultSelectedReason = null,
    note: noteProp,
    onNoteChange,
    onReasonChange,
    onNext,
  }: Step1Props) => {
    const { t } = useTranslation('shuffle');
    const [internalSelectedReason, setInternalSelectedReason] =
      useState<ReschedulingReason | null>(defaultSelectedReason);
    const [internalNote, setInternalNote] = useState('');

    const isReasonControlled = selectedReason !== undefined;
    const isNoteControlled = noteProp !== undefined;
    const currentSelectedReason = isReasonControlled ? selectedReason : internalSelectedReason;
    const note = isNoteControlled ? noteProp : internalNote;

    const handleReasonChange = useCallback(
      (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
        const reason = value as ReschedulingReason;
        if (!isReasonControlled) {
          setInternalSelectedReason(reason);
        }
        onReasonChange?.(reason);
      },
      [isReasonControlled, onReasonChange]
    );

    const handleNoteChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const nextNote = event.target.value;
        if (!isNoteControlled) {
          setInternalNote(nextNote);
        }
        onNoteChange?.(nextNote);
      },
      [isNoteControlled, onNoteChange]
    );

    const isNoteRequired = currentSelectedReason === 'other';
    const hasValidNote = note.trim().length > 0;
    const canProceed = Boolean(currentSelectedReason) && (!isNoteRequired || hasValidNote);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
          mt: 2,
        }}
      >
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography variant="h6">{t('createAvailability.title')}</Typography>
        </Box>

        <Box sx={{ px: 2, pb: 2 }}>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ mb: 0.75, fontWeight: 600 }}
          >
            {t('step1.subtitle')}
          </Typography>
          <RadioGroup
            value={currentSelectedReason ?? ''}
            onChange={handleReasonChange}
            aria-label={t('step1.subtitle')}
          >
            {RESCHEDULING_REASONS.map((reason) => {
              const { title, description } = REASON_TRANSLATION_KEYS[reason];

              return (
                <FormControlLabel
                  key={reason}
                  value={reason}
                  control={<Radio size="small" sx={{ p: 1 }} />}
                  sx={{ mx: 0, alignItems: 'center' }}
                  label={
                    <Typography variant="body2" component="span">
                      <Box component="span" color="text.primary">
                        {t(title)}
                      </Box>
                      {description ? (
                        <Box component="span" color="text.secondary">
                          {` — ${t(description)}`}
                        </Box>
                      ) : null}
                    </Typography>
                  }
                />
              );
            })}
          </RadioGroup>

          <TextField
            multiline
            fullWidth
            required={isNoteRequired}
            value={note}
            onChange={handleNoteChange}
            placeholder={t('step1.notePlaceholder')}
            error={isNoteRequired && !hasValidNote}
            helperText={
              isNoteRequired && !hasValidNote ? t('step1.noteRequired') : undefined
            }
            sx={{
              mt: 0.5,
              mb: 2,
              '& .MuiInputBase-root': {
                minHeight: 80,
                alignItems: 'flex-start',
                py: 1.125,
              },
              '& .MuiInputBase-inputMultiline': {
                minHeight: '58px !important',
              },
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
          <Button
            variant="contained"
            size="small"
            disabled={!canProceed}
            onClick={onNext}
            sx={{
              minWidth: 64,
              height: 30,
              px: 1,
            }}
          >
            {t('step1.next')}
          </Button>
        </Box>
      </Box>
    );
  }
);

Step1.displayName = 'Step1';
